import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction'; // Needed for event click
import { AddAppointmentModal } from '../FullCalendar/AddAppointment';
import { EditAppointmentModal } from '../FullCalendar/EditAppointments';
import React, { useState, useEffect } from 'react';
import { fetchAppointmentsByResource, fetchPractitionersByPractices } from '../../api';
import { Col, Spinner } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';


export default function UserSchedule({ userId, refresh }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editModalShow, setEditModalShow] = useState(false);
    const [addModalShow, setAddModalShow] = useState(false);
    const [resourcesTitle, setResourcesTitle] = useState();
    const [practiceIds, setPracticeIds] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [resourcesId, setResourcesId] = useState(null);
    const [resourcesName, setResourcesName] = useState(null);

    useEffect(() => {
        async function getEventsAndAvailability() {
            if (!userId) return;

            try {
                const appointmentsResponse = await fetchAppointmentsByResource(userId);
                const formattedEvents = appointmentsResponse.map(event => ({
                    id: event._id || `event-${Math.random()}`,
                    title: event.title || "Untitled Event",
                    startTime: event.startTime,
                    endTime: event.endTime,
                    start: event.start || null,
                    end: event.end || null,
                    daysOfWeek: event.daysOfWeek || null,
                    resourceId: event.resourceId || "unknown-resource",
                    resourceName: event.resourceName || "Unknown Resource",
                    practiceId: event.practiceId || "0000",
                    extendedProps: {
                        status: event.status || "N/A",
                        patientFirstName: event.patientFirstName || "N/A",
                        patientLastName: event.patientLastName || "N/A",
                        patientEmail: event.patientEmail || "N/A",
                        notes: event.notes || "N/A",
                        serviceId: event.serviceId || "N/A",
                        patientId: event.patientId || "N/A"
                    },
                    color: event.backgroundColor || "var(--acc-brand-primary, #0f3490)"
                }));

                setEvents(formattedEvents);

                const token = sessionStorage.getItem('user');
                if (!token) {
                    console.error('No user token found in session storage.');
                    return;
                }

                const decoded = jwtDecode(token);
                setPracticeIds(decoded.practice);

                const practitionersResponse = await fetchPractitionersByPractices(decoded.practice);
                setResourcesTitle(practitionersResponse);

            } catch (error) {
                console.error("Error fetching data:", error.message);
            } finally {
                setLoading(false);
            }
        }

        getEventsAndAvailability();
    }, [userId, refresh]);

    const handleEventClick = (clickInfo) => {
        setSelectedEvent({
            id: clickInfo.event._def.publicId || "N/A",
            title: clickInfo.event.title || "N/A",
            status: clickInfo.event.extendedProps.status || "N/A",
            patientFirstName: clickInfo.event.extendedProps.patientFirstName || "N/A",
            patientLastName: clickInfo.event.extendedProps.patientLastName || "N/A",
            patientId: clickInfo.event.extendedProps.patientId || "N/A",
            patientEmail: clickInfo.event.extendedProps.patientEmail || "N/A",
            notes: clickInfo.event.extendedProps.notes || "N/A",
            start: clickInfo.event.startStr || "N/A",
            end: clickInfo.event.endStr || "N/A",
            resourceId: userId || "N/A",
            resourceName: clickInfo.event._def.resourceIds
                ? resourcesTitle.find(r => r.id === userId)?.title || "N/A"
                : "N/A",
            practiceId: clickInfo.event.extendedProps.practiceId || "N/A",
            serviceId: clickInfo.event.extendedProps.serviceId || "N/A",
        });
        const foundResource = resourcesTitle.find(resource => resource.id === userId);
        setResourcesId(userId);
        setResourcesName(foundResource?.title || "Unknown");
    };

    useEffect(() => {
        if (selectedEvent && selectedEvent.resourceId !== "N/A") {
            setEditModalShow(true);
        }
    }, [selectedEvent]);

    const dayClickAction = (info) => {
        setSelectedDate(info.dateStr);

        const foundResource = resourcesTitle.find(resource => resource.id === userId);
        setResourcesId(userId);
        setResourcesName(foundResource?.title || "Unknown");
        setAddModalShow(true);
    };


    const handleAddEvent = (newEvent) => {
        setEvents((prevEvents) => [...prevEvents, newEvent]);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading schedule…</p>
            </div>
        );
    }

    return (
        <div className="pt-1 d-flex">
            <Col md={12}>
                <div className="d-flex flex-column">
                    <FullCalendar
                    schedulerLicenseKey="0328351339-fcs-1748930365"
                    key={`calendar-${userId}-${refresh}-${events.length}`}
                        plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                        nowIndicator={true}
                        businessHours={false}
                        slotDuration={'00:30:00'}

                        slotMinTime={'06:00:00'}
                        slotMaxTime={'20:00:00'}
                        initialView="timeGridDay"
                        height="auto"
                        views={{
                            customFourDayView: {
                              type: "timeGrid",
                              duration: { days: 7 },
                              buttonText: "7 Days",
                            },
                          }}
                        buttonText={{
                            today: "Today",
                            month: "Month",
                            week: "Week",
                            day: "Day",
                          }}
                        headerToolbar={{
                            left: "prev,next today",
                            center: "title",
                            right: "timeGridDay,customFourDayView",
                          }}
                          events={events.length > 0 ? events : []}
                        eventClick={handleEventClick}
                        editable={true}
                        dateClick={dayClickAction}
                    />
                </div>
            </Col>

            {selectedEvent && (
            <EditAppointmentModal
                event={selectedEvent}
                show={editModalShow}
                resources={resourcesTitle}
                onHide={() => setEditModalShow(false)}
            />
            )}
            {selectedDate && (
             <AddAppointmentModal
                show={addModalShow}
                onHide={() => setAddModalShow(false)}
                selectedDate={selectedDate}
                resourcesId={userId}
                resourcesName={resourcesName}
                onSave={handleAddEvent}
                events={events}
            />
            )}
        </div>
    );
}
